import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {ConfigurationInterpreter} from '@viamedici-spc/configurator-ts';

import store from '../../store';
import {conditional} from '../../util/conditional';
import {getProductData} from '../../util/getProductData';
import {filterAndJoin} from '../../util/filterAndJoin';
import {getValueOfFeature, hasOnlyThisFeatureValue, pimText, pimTextDebug} from '../../util/values';
import {
	selectDimensionHeight,
	selectDimensionWidth,
	selectDimensionDepth,
	selectDimensionDiameter,
	selectWeight,
	selectWindsurfacearea,
	selectSpcConfiguration,
	selectSpcAttributes,
	selectProductData,
	selectSystemLumen,
	selectDimmLevel,
	selectCoefficients,
	selectHasCoefficients, selectSectionConfig, selectFilteredSpcAttributes
} from '../../selectors';
import {attributeIdToString, stringToAttributeId} from '../spc/util/spcUtils';
import {regionCheck} from '../../util/regionCheck';
import {calculateCurrent, calculatePower} from '../../util/dimLevel';

@customElement('shared-technical-data')
export class SharedTechnicalData extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
			}
			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]
	;

	constructor() {
		super();
		this.productData = null;
		this.satisfiedAttributes = [];
		this.unSatisfiedAttributes = [];
		store.subscribe(() => this.requestUpdate());

	}

	onlyNone(key) {
		if (!key.includes('::')) {
			key = 'root::' + key;
		}
		const attributeOptions = selectSectionConfig(this.state)[0].attributeOptions ?? null;
		if (attributeOptions && attributeOptions.length > 0) {
			const checkForOnlyNone = attributeOptions[key]?.doNotShowWhenNoneIsOnlyOption ?? false;
			return checkForOnlyNone && hasOnlyThisFeatureValue(key, 'None', this.spcConfiguration);
		} else {
			return false;
		}
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.debugShowAll = state.status.debugShowAll;
		this.lang = state.status.lang;
		this.lightProfile = state.lightProfile.data;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.productData = selectProductData(this.state);
		this.spcConfiguration = selectSpcConfiguration(state);
		this.spcAttributes = selectFilteredSpcAttributes(state);
		if (this.spcAttributes?.length > 0 && this.productData) {
			//this.spcAttributes = this.spcAttributes
				//.filter(spcAttribute => {
				//	const featureProductData = getProductData(spcAttribute.id.localId, this.productData);
				//	return this.debugShowAll || featureProductData?.isVisible && regionCheck(featureProductData, this.region);
				//}) // filter when configured to not be visible in PIM
				//.filter(spcAttribute => !this.onlyNone(spcAttribute.id.localId)) // filter when None is the only option and filter is enabled in attributeOptions
				//.sort((a, b) =>
				//	getProductData(a.id.localId, this.productData).displaySequenceUi -
				//	getProductData(b.id.localId, this.productData).displaySequenceUi
				//);
			this.satisfiedAttributes = this.spcAttributes.filter(attribute => attribute.isSatisfied);
			this.unSatisfiedAttributes = this.spcAttributes.filter(attribute => !attribute.isSatisfied);
		}
	}

	getValueFromProductData(attributeKey, featureValueId) {
		const pd = getProductData(attributeKey, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(attributeKey, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, attributeKey, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, attributeKey, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		console.log('render sharedTechnicalData', this.satisfiedAttributes);
		const s = this.state;
		const lightEngineDescription = ''; //this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV');
		const opticalVersion = this.t('LightEngine_1::LE_OpticalVersion', 'FV.S');
		const reflectorColour = this.t('LightEngine_1::LE_ReflectorColour', 'FV.S');
		const isLightEngineDescriptionPresent = lightEngineDescription || opticalVersion || reflectorColour;

		const controlNoWire = this.t('root::EBlock_1::EB_ControlNoWire', 'FV.S');
		const controlWire = this.t('root::EBlock_1::EB_ControlWire', 'FV.S');
		const isControlPresent = controlNoWire || controlWire;

		const dimensionsLabel = ''; //this.t('TE_Dimensions', 'D');
		const dimensionDiameter = selectDimensionDiameter(s);
		const dimensionDepth = selectDimensionDepth(s);
		const dimensionWidth = selectDimensionWidth(s);
		const dimensionHeight = selectDimensionHeight(s);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight;

		// const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV.S');
		// const wattageMax = this.c('REDTAB.RETURN_EB_Wattage_Max');

		const powerLabel = {
			en: 'Power',
			fr: 'Puissance',
			de: 'Leistung',
		};
		const currentLabel = {
			en: 'Current',
			fr: 'Actuel',
			de: 'Stromstärke',
		};

		let maxLumenPerWatt = 0;
		let maxLumenValue = 0;
		let dimmedLumen = 0;
		let maxWatt = 0;
		let dimmedLumenPerWatt = 0;

		if (this.spcConfiguration) {
			maxLumenValue = parseInt(getValueOfFeature('root::LightEngine_1::LE_DimmingLevel', this.spcConfiguration), 10);
		}
		dimmedLumen = parseInt(selectDimmLevel(s), 10);
		dimmedLumenPerWatt = Math.round(
			dimmedLumen /
			calculatePower(dimmedLumen, selectCoefficients(s).formular2.coefficients) * 100
		) / 100;

		maxWatt = parseInt(this.c('REDTAB.RETURN_EB_Wattage_Max'), 10) * 100;
		if (selectCoefficients(s)) {
			maxWatt = calculatePower(maxLumenValue, selectCoefficients(s).formular2.coefficients);
		}
		maxLumenPerWatt = Math.round((maxLumenValue / maxWatt) * 100 ) / 100;

		return html`
			${when(this.satisfiedAttributes.length > 0, () => html`
				<h3 class="subheadline">${msg('Selected Properties')}</h3>
				${repeat(this.satisfiedAttributes,
					(attribute) => attribute.key,
					(attribute) => html`
						<shared-specsheet-feature condition="${this.t(attribute.key, 'FV.S')}">
							<trans-label slot="label" key="${attribute.key}" context="F"></trans-label>
							<spc-values-label slot="value" class="value" display="selected" attributeKey="${attribute.key}" context="FV.S"></spc-values-label>
						</shared-specsheet-feature>
					`)}
			`)}

			<h3 class="subheadline">${msg('Light Output')}, ${msg('System Power')}</h3>

			<shared-specsheet-feature>
				<span slot="label">${msg('max')}. <trans-label slot="label" key="LE_DimmingLevel_1" context="F"></trans-label></span>
				<span slot="value">
					<spc-values-label attributeKey="LE_DimmingLevel_1" display="selected" context="FV.S"></spc-values-label>
					${when(!isNaN(maxLumenPerWatt) && maxLumenPerWatt !== Infinity, () => html`, ${maxLumenPerWatt} lm/W`)}
				</span>
			</shared-specsheet-feature>

			${when(maxWatt && maxWatt > 0, () => html`
				<shared-specsheet-feature>
					<span slot="label">${msg('max')}. <trans-label slot="label" key="LU_SystemWattage" context="D"></trans-label></span>
					<span slot="value">${maxWatt} W</span>
				</shared-specsheet-feature>
			`)}

			${when(selectHasCoefficients(s) && dimmedLumen !== maxLumenValue,
				() => html`
					<shared-specsheet-feature>
						<span slot="label">${msg('dimmed luminous flux')}</span>
						<span slot="value">${dimmedLumen} lm,
						${dimmedLumenPerWatt} lm/W</span>
					</shared-specsheet-feature>
					<shared-specsheet-feature>
						<span slot="label">${msg('dimmed power')}</span>
						<span slot="value">${calculatePower(dimmedLumen, selectCoefficients(s).formular2.coefficients)} W</span>
					</shared-specsheet-feature>
				`)}
			${when(selectHasCoefficients(s),
				() => html`
					<shared-specsheet-feature>
						<span slot="label">${currentLabel[this.lang]}</span>
						<span slot="value">${calculateCurrent(dimmedLumen, selectCoefficients(s).formular1.coefficients)} mA</span>
					</shared-specsheet-feature>
				`)}

			${when(this.unSatisfiedAttributes.length > 0, () => html`
				<h3 class="subheadline">${msg('Other Available Properties')}</h3>
				${repeat(this.unSatisfiedAttributes,
					(attribute) => attribute.key,
					(attribute) => html`
						<shared-specsheet-feature>
							<trans-label slot="label" key="${attribute.key}" context="F"></trans-label>
							<spc-values-label slot="value" class="value" display="selected" attributeKey="${attribute.key}" context="FV.S"></spc-values-label>
						</shared-specsheet-feature>
					`)}
			`)}

			<h3 class="subheadline">${msg('Attributes')}</h3>
			<shared-technical-data-attributes></shared-technical-data-attributes>
        `;
		// <shared-technical-data-attributes></shared-technical-data-attributes>
	}
}
